<template>
  <sp-dialog
    class="sp-confirm"
    :class="classModifiers"
    :open.prop="isOpen"
    no-backdrop
    :persistent="persistentAttribute"
    :ignore-outside-click="ignoreOutsideClickAttribute"
    lock-scroll
    v-bind="dialogAttrs"
    @close="handleDialogClose"
  >
    <div class="sp-confirm__header">
      <div class="sp-confirm__title">
        <slot name="title">
          {{ title }}
        </slot>
      </div>

      <div @click="close">
        <slot name="close">
          <sp-button v-if="isCloseable" class="sp-confirm__close" label="x" variant="text-inline"></sp-button>
        </slot>
      </div>
    </div>

    <div class="sp-confirm__content">
      <slot>
        {{ content }}
      </slot>
    </div>

    <div class="sp-confirm__actions">
      <div @click="cancel">
        <slot name="cancel">
          <sp-button v-if="isCancelable" color="secondary" class="sp-confirm__action-cancel">
            {{ cancelLabel }}
          </sp-button>
        </slot>
      </div>

      <div @click="confirm">
        <slot name="confirm">
          <sp-button color="primary" class="sp-confirm__action-confirm">
            {{ confirmLabel }}
          </sp-button>
        </slot>
      </div>
    </div>
  </sp-dialog>
</template>

<script setup>
import { useStorage } from "@vueuse/core";
import { computed, ref, useAttrs, useSlots, watch } from "vue";
import { omitAttrs } from "#ce/utils/forwarded-attrs";
import { isPresent, toBoolean } from "#ce/utils/props";
import { props as confirmProps } from "./props.js";

const slots = useSlots();
const emit = defineEmits(["confirm", "cancel"]);

defineOptions({ inheritAttrs: false });

const props = defineProps(confirmProps);
const attrs = useAttrs();
const title = computed(() => attrs.title);
const dialogAttrs = computed(() => omitAttrs(attrs, ["title"]));

const isCloseable = computed(() => isPresent(props.closeable));
const isCancelable = computed(() => isPresent(props.cancelable));
const cancelsOnEscape = computed(() => toBoolean(props.cancelOnEscape));
// Present as "" or absent, so the dialog reads the same attribute an author would write.
const persistentAttribute = computed(() => (cancelsOnEscape.value ? null : ""));
const ignoreOutsideClickAttribute = computed(() => (cancelsOnEscape.value ? "" : null));

const classModifiers = computed(() => ({
  "--closeable": isCloseable.value || slots.close,
  "--cancelable": isCancelable.value || slots.cancel,
}));

const isConfirmed = props.persist ? useStorage(props.persist, false) : ref(false);

function shouldOpen(open) {
  if (open && props.persist) {
    return !isConfirmed.value;
  }
  return open;
}

const isOpen = ref(shouldOpen(toBoolean(props.open)));

watch(
  () => props.open,
  (value) => (isOpen.value = shouldOpen(toBoolean(value))),
);

function cancel() {
  close();
  emit("cancel");
}

function confirm() {
  isConfirmed.value = true;
  close();
  emit("confirm");
}

function close() {
  isOpen.value = false;
}

// With cancel-on-escape, the dialog closes itself only on Escape, while it is still open here.
function handleDialogClose() {
  if (cancelsOnEscape.value && isOpen.value) {
    cancel();
  }
}
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-confirm {
  &.--closeable {
    // TODO: @mgruschetzki: add close button definition
  }
  &.--cancelable {
    // TODO: @mgruschetzki: add cancel button definition
  }
}

.sp-confirm__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid var(--sp-comp-confirm-border-color, var(--sp-sys-color-outline-variant));
}

.sp-confirm__title {
  font-size: 1.25rem;
  font-weight: 500;
  // TODO: @mgruschetzki: add definition
}

.sp-confirm__close {
  // TODO: @mgruschetzki: add close button definition
}

.sp-confirm__content {
  padding: 1rem;
  // TODO: @mgruschetzki: add definition
}

.sp-confirm__actions {
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
  // TODO: @mgruschetzki: add definition
}

.sp-confirm__action-cancel {
  // TODO: @mgruschetzki: add cancel button definition
}

.sp-confirm__action-confirm {
  // TODO: @mgruschetzki: add confirm button definition
}
</style>
